'use strict';
import React, { useState, useEffect, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import panelActions from '../../../redux/panelAction';
import categoryActions from '../../../redux/categoryActions';
import marketplaceActions from '../../../redux/marketplaceActions';
import inboxActions from '../../../redux/inboxAction';
import searchActions from '../../../redux/searchActions';
import userActions from '../../../redux/userActions';
import Toastr from 'toastr';
import commonModule from '../../../public/js/common';
import ReactGA from 'react-ga';
import { ResponsiveAppBar } from '../header/app-bar';
import { signOut } from '@src/utils/signOut';
import { TcppModal } from '../header/tcpp-modal';
import { OnBoardingRoleSelect } from '../header/onboarding-role-select';
import { SmsSubscription } from '../header/sms-subscription';
import { ConfirmationDialog } from '@src/components/common/dialogs/confirmation-dialog';
import { setURLState } from '@src/utils/setURLState';
import { HeaderLayoutBottomComponent } from './header-bottom';
import PropTypes from 'prop-types';

export const HeaderLayout = ({ showSearchHeader = true }) => {
    const dispatch = useDispatch();

    const categories = useSelector(state => state.categoryReducer.categories);
    const user = useSelector(state => state.userReducer.user);
    const panels = useSelector(state => state.panelsReducer.panels);
    const logoUrl = useSelector(state => state.marketplaceReducer.logoUrl);
    const unreadCount = useSelector(state => state.inboxReducer.unreadCount);
    const isPrivateEnabled = useSelector(state => state.marketplaceReducer.isPrivateEnabled);
    const isMerchantRestrictedOnly = useSelector(state => state.marketplaceReducer.isMerchantRestrictedOnly);
    const googleAnalytics = useSelector(state => state.marketplaceReducer.googleAnalytics);

    // Local state
    const [keyword, setKeyword] = useState('');
    const [showTcppModal, setShowTcppModal] = useState(false);
    const [showSmsSubscription, setShowSmsSubscription] = useState(false);
    const [showRoleSelectModal, setShowRoleSelectModal] = useState(false);
    const [hasNewTask, setHasNewTask] = useState(false);

    // --- Helper functions ---
    const showMessage = toastStr => {
        if (toastStr.type === 'error') {
            Toastr.error(toastStr.body, toastStr.header);
        } else if (toastStr.type === 'success') {
            Toastr.success(toastStr.body, toastStr.header);
        }

        // Prevent repeat
        const url = new URL(window.location);
        url.searchParams.delete('error');
        window.history.replaceState({}, document.title, url);
    };

    const verifyTCPP = data => {
        let showModal = true;
        if (data.CustomFields && data.CustomFields.length > 0) {
            const showTermsCondition = !data.CustomFields.some(r => r.Code.includes('-TermsConditions-'));
            const showPrivacyPolicy = !data.CustomFields.some(r => r.Code.includes('-PrivacyPolicy-'));
            const showServiceQualityWaiver = !data.CustomFields.some(r => r.Code.includes('-ServiceQualityWaiver-'));
            showModal = showTermsCondition || showPrivacyPolicy || showServiceQualityWaiver;
        }
        setShowTcppModal(showModal);
    };

    const loadGoogleAnalytics = () => {
        if (googleAnalytics) {
            ReactGA.initialize(googleAnalytics['google-trackid']);
            ReactGA.pageview(window.location.pathname + window.location.search);
        }
    };

    const bindWindowClick = () => {
        if (typeof window === 'undefined') return;
        const $menu = window.$('.h-st-menus');
        window.$(document).mouseup(e => {
            if (!$menu.is(e.target) && $menu.has(e.target).length === 0) {
                $menu.hide();
            }
        });
        window.$(window).on('click', () => {
            window.$('.h-dd-menu').hide();
        });
    };

    const searchMobile = () => {
        window.$('.header-bottom ul.header-menus > li.h-search').toggle();
        if (window.$('.header-bottom ul.header-menus > li.h-search').css('display') === 'list-item') {
            window
                .$('.h-mobi-search.mobi-show')
                .find('i')
                .removeClass('fa-search')
                .addClass('fa-times');
        } else {
            window
                .$('.h-mobi-search.mobi-show')
                .find('i')
                .removeClass('fa-times')
                .addClass('fa-search');
        }
    };

    useEffect(() => {
        if (!panels || panels.length === 0) {
            dispatch(panelActions.asyncLoadingPanels());
        }
        if (!categories || categories.length === 0) {
            dispatch(categoryActions.asyncLoadingCategories());
        }
        if (!logoUrl) {
            dispatch(marketplaceActions.getInfo());
        }

        if (user) {
            dispatch(inboxActions.getUnreadCount());

            if (!user.Guest) {
                dispatch(
                    userActions.checkUserDetails(data => {
                        verifyTCPP(data);
                    })
                );
            }
        }

        if (window.location.search !== '') {
            const urlParams = new URLSearchParams(window.location.search);
            let keywordVal = '';
            let hasNewTaskVal = false;

            if (urlParams.has('keywords')) {
                const rawKeywords = urlParams.get('keywords') ?? '';

                try {
                    keywordVal = decodeURIComponent(raw);
                } catch {
                    keywordVal = rawKeywords;
                }
            }

            if (urlParams.has('hasNewTask')) {
                hasNewTaskVal = true;
                setURLState({ hasNewTask: null });
            }

            setKeyword(keywordVal);
            setHasNewTask(hasNewTaskVal);
        }

        const urlParams = new URLSearchParams(window.location.search);
        const error = urlParams.get('error');
        if (error) {
            showMessage({ type: 'error', body: error, header: 'Sorry!' });
        }

        bindWindowClick();
        commonModule.init();

        if (googleAnalytics) {
            loadGoogleAnalytics();
        }
    }, [dispatch, user]);

    useEffect(() => {
        commonModule.initHeaderMenuScroll();
    });

    return (
        <Fragment>
            <ResponsiveAppBar
                unreadCount={unreadCount}
                user={user}
                signOut={signOut}
                isPrivateEnabled={isPrivateEnabled}
                isMerchantRestrictedOnly={isMerchantRestrictedOnly}
                getUnreadCount={() => dispatch(inboxActions.getUnreadCount())}
            />
            {showSearchHeader && (
                <HeaderLayoutBottomComponent
                    keyword={keyword}
                    panels={panels}
                    searchMobile={searchMobile}
                    searchGooglePlaces={(keyword, cb) => dispatch(searchActions.searchGooglePlaces(keyword, cb))}
                    user={user}
                />
            )}
            {showTcppModal && (
                <TcppModal
                    open={showTcppModal}
                    onClose={() => setShowTcppModal(false)}
                    onShowSmsSubscription={() => setShowSmsSubscription(true)}
                />
            )}
            {showRoleSelectModal && <OnBoardingRoleSelect />}
            {showSmsSubscription && (
                <SmsSubscription
                    onClose={() => {
                        setShowRoleSelectModal(true);
                        setShowSmsSubscription(false);
                    }}
                />
            )}
            {hasNewTask && (
                <ConfirmationDialog
                    open={hasNewTask}
                    onAgree={() => {
                        location.href = '/merchants/order/history';
                    }}
                    agreeLabel='GO TO BOOKED TASKS PAGE'
                    content={{
                        body: 'New Task Request',
                        description: 'Please go to your Purchase Order page and confirm the new task request.'
                    }}
                />
            )}
        </Fragment>
    );
};

HeaderLayout.propTypes = {
    showSearchHeader: PropTypes.bool
};
